import { apiDataProtection } from "@/api/data-protection.api.ts";
import { useModals, FontAwesomeIcon, ListOption, Text } from "@skailer-com/uikit";
import useDataProtectionHook from "@/hooks/use-data-protection.hook.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApplicantDataProtectionSenderModal } from "@/pages/recruit/candidates/widgets/ApplicantDataProtectionSenderModal.tsx";
import { useEmailIntegrations } from "@/queries/integrations/use-emails.query.ts";
import { DataProtectionIncreasePeriod } from "@/pages/settings/data-protection/index/widgets/DataProtectionIncreasePeriod.tsx";
import { applicantsKeys } from "@/queries/recruit/use-applicants.query.ts";
import { dataProtectionKeys } from "@/queries/use-data-protection.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { Rounding } from "@/types/common.types.ts";
import {
  ApiApplicantDataProtection,
  DataProtectionObjectTypes,
  DataProtectionState,
  DataProtectionTypes,
} from "@/types/data-protection.types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { ReactElement, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ApplicantDataProtectionOptionProps {
  dataProtection: ApiApplicantDataProtection;
  applicantId: number;
}

export function ApplicantDataProtectionOption({
  dataProtection,
  applicantId,
}: ApplicantDataProtectionOptionProps) {
  const { t } = useTranslation();
  const { getDaysToDate } = useFormatter();
  const { getTitleByType } = useDataProtectionHook();
  const queryClient = useQueryClient();
  const { handleErrors, onError } = useErrorHandle();
  const { refetch: refetchEmailIntegrations } = useEmailIntegrations();
  const [isRequesting, setIsRequesting] = useState(false);
  const { openModal } = useModals();

  const getIconForListByState = (state: DataProtectionState): ReactElement => {
    switch (state) {
      case DataProtectionState.requested:
        return <FontAwesomeIcon icon="fa-light fa-clock-rotate-left text-warning" />;
      case DataProtectionState.given:
        return <FontAwesomeIcon icon="fa-light fa-circle-check text-positive" />;
      case DataProtectionState.expiring:
        return <FontAwesomeIcon icon="fa-light fa-circle-check text-warning" />;
      case DataProtectionState.expired:
        return <FontAwesomeIcon icon="fa-light fa-circle-exclamation text-danger" />;
      case DataProtectionState.declined:
        return <FontAwesomeIcon icon="fa-light fa-circle-exclamation text-danger" />;
      case DataProtectionState.empty:
      default:
        return <FontAwesomeIcon icon="fa-light fa-paper-plane" />;
    }
  };

  const getStatusForListByState = (data: ApiApplicantDataProtection): string => {
    switch (data.state) {
      case DataProtectionState.requested:
        return t("data_protection:status_request_requested", {
          count_days: t("plurals:plural_day", { count: getDaysToDate(data.datetime_state_start) }),
        });
      case DataProtectionState.given:
        return t("data_protection:status_request_given", {
          count_days: t("plurals:plural_day", {
            count: getDaysToDate(data.datetime_state_end, Rounding.up),
          }),
        });
      case DataProtectionState.expiring:
        return t("data_protection:status_request_given", {
          count_days: t("plurals:plural_day", {
            count: getDaysToDate(data.datetime_state_end, Rounding.up),
          }),
        });
      case DataProtectionState.expired:
        return t("data_protection:status_request_expired", {
          count_days: t("plurals:plural_day", { count: getDaysToDate(data.datetime_state_start) }),
        });
      case DataProtectionState.declined:
        return t("data_protection:status_request_declined", {
          count_days: t("plurals:plural_day", { count: getDaysToDate(data.datetime_state_start) }),
        });
      case DataProtectionState.empty:
      default:
        return t("data_protection:status_request_not_sent");
    }
  };

  const getTitleByProviderAndState = (
    state: DataProtectionState,
    type: DataProtectionTypes
  ): ReactNode => {
    switch (state) {
      case DataProtectionState.requested:
        return (
          <Text className={"text-warning"}>
            {t("data_protection:title_request_requested", { type_title: getTitleByType(type) })}
          </Text>
        );
      case DataProtectionState.given:
        return (
          <Text className={"text-positive"}>
            {t("data_protection:title_request_given", { type_title: getTitleByType(type) })}
          </Text>
        );
      case DataProtectionState.expiring:
        return (
          <Text className={"text-warning"}>
            {t("data_protection:title_request_expiring", { type_title: getTitleByType(type) })}
          </Text>
        );
      case DataProtectionState.expired:
        return (
          <Text className={"text-danger"}>
            {t("data_protection:title_request_expired", { type_title: getTitleByType(type) })}
          </Text>
        );
      case DataProtectionState.declined:
        return (
          <Text className={"text-danger"}>
            {t("data_protection:title_request_declined", { type_title: getTitleByType(type) })}
          </Text>
        );
      case DataProtectionState.empty:
      default:
        return (
          <Text>
            {t("data_protection:title_request_empty", { type_title: getTitleByType(type) })}
          </Text>
        );
    }
  };

  const sendConsentRequest = async (type: DataProtectionTypes, fromEmail: string) => {
    const { success, error } = await apiDataProtection.consentRequest({
      from_email: fromEmail,
      object_id: applicantId,
      object_type: DataProtectionObjectTypes.applicant,
      data_protection_type: type,
    });

    if (success) {
      if (DataProtectionObjectTypes.applicant === "applicant") {
        queryClient.invalidateQueries({
          queryKey: applicantsKeys.detail(applicantId).queryKey,
        });
      }

      queryClient.invalidateQueries({
        queryKey: dataProtectionKeys.searchRequests._def,
      });

      analyticsService.trackEvent(analyticEvents.dataProtection.requestSent, {
        [analyticProperties.id]: applicantId,
      });
    } else handleErrors(error);

    return success;
  };

  const onClickDataProtection = async (type: DataProtectionTypes) => {
    if (isRequesting) return;
    setIsRequesting(true);
    try {
      const { data: integrations, error } = await refetchEmailIntegrations();
      if (error) {
        onError(error);
        return;
      }

      const emails =
        integrations
          ?.filter((integration) => integration.is_active)
          .map((integration) => integration.identity) ?? [];

      if (emails.length === 1) {
        await sendConsentRequest(type, emails[0]);
      } else {
        openModal(ApplicantDataProtectionSenderModal, {
          emails,
          requestTitle: t("data_protection:title_request_empty", {
            type_title: getTitleByType(type),
          }),
          onSend: (fromEmail: string) => sendConsentRequest(type, fromEmail),
        });
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const increasePeriod = () => {
    openModal(DataProtectionIncreasePeriod, {
      objectId: applicantId,
      type: dataProtection.type,
    });
  };

  return (
    <>
      <ListOption
        disabled={isRequesting}
        onClick={() => {
          onClickDataProtection(dataProtection.type);
        }}
        key={`protection-${dataProtection.type}`}
        leftIcon={getIconForListByState(dataProtection.state)}
        titleSlot={getTitleByProviderAndState(dataProtection.state, dataProtection.type)}
        caption={getStatusForListByState(dataProtection)}
      />
      {(dataProtection.state === DataProtectionState.given ||
        dataProtection.state === DataProtectionState.expiring) && (
        <ListOption
          onClick={() => {
            increasePeriod();
          }}
          key={`protection-manual-${dataProtection.type}`}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-gear" />}
          title={t("data_protection:increase_manually_title")}
          caption={t("data_protection:increase_manually_description")}
        />
      )}
    </>
  );
}
